import React from "react";

type Props = { children: React.ReactNode };
type State = { error: Error | null };

/**
 * Root error boundary — catches render-time crashes (the "white screen of
 * death") and shows a recoverable reload screen instead. Deliberately
 * framework-free (no MUI/theme/intl) so it still renders if the crash
 * happened inside a provider.
 *
 * Network errors are NOT handled here — that's the data layer's job
 * (see utils/transient-error.ts + components/service-unavailable).
 */
class AppErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error("[AppErrorBoundary]", error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;

    const fr = !navigator.language?.startsWith("en");
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          gap: 16,
          padding: 24,
          textAlign: "center",
          fontFamily: "inherit",
        }}
      >
        <div style={{ fontSize: 40 }}>⚠️</div>
        <div style={{ fontSize: 24, fontWeight: 700 }}>
          {fr ? "Une erreur est survenue" : "Something went wrong"}
        </div>
        <div style={{ fontSize: 14, color: "#6b7280", maxWidth: 480 }}>
          {fr
            ? "Votre session est conservée. Rechargez la page pour continuer."
            : "Your session is preserved. Reload the page to continue."}
        </div>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: "10px 28px",
            borderRadius: 8,
            border: "none",
            background: "#5a28ac",
            color: "#fff",
            fontSize: 14,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          {fr ? "Recharger la page" : "Reload the page"}
        </button>
      </div>
    );
  }
}

export default AppErrorBoundary;
